import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import api from '../../services/api'
import { hasPermission } from '../../utils/hasPermission'
import { createQueryString } from '../../utils/queryParams'
import {
  USER_PERMISSIONS,
  SIZES,
  PRESSURE_MONITOR_MEASUREMENT_UNITS,
  PRESSURE_MONITOR_ALERT_TYPE_LABELS,
  PRESSURE_MONITOR_ALERT_TYPE_DESCRIPTIONS,
} from '../../constants'

import { H3 } from '../common/Headers'
import {
  Spin,
  Switch,
  AntSelect,
  Table,
  Column,
  Icon,
  Tooltip,
  Popover,
} from '../common/Ant'
import { Option } from '../common/Select'
import { Anchor } from '../common/Anchor'
import Button from '../common/Button'
import Block from '../common/Block'
import SiteUsers from '../SiteUsers'

const audioAlertStatus = audioAlert =>
  audioAlert ? (
    <Tooltip title="Audio Alert Enabled">
      <Icon type="alert" className="gold" />
      <span className="pl2">Enabled</span>
    </Tooltip>
  ) : (
    <Tooltip title="Audio Alert Disabled">
      <Icon type="block" className="gray" />
      <span className="pl2">Disabled</span>
    </Tooltip>
  )

const PressureMonitorAlertsSettings = ({
  siteSlug,
  siteUsers,
  getAllUsersBySite,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [recipients, setRecipients] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(undefined)
  const [pressureMonitors, setPressureMonitors] = useState([])
  const [isPressureMonitorsLoading, setIsPressureMonitorsLoading] = useState(
    true
  )
  const [showUnassigned, setShowUnassigned] = useState(false)

  const canEdit = hasPermission(USER_PERMISSIONS.EDIT_PRESSURE_MONITOR)

  const getSettings = useCallback(() => {
    api
      .getPressureMonitorAlertSettings(siteSlug)
      .then(data => {
        setRecipients(data.recipients)
        setSubscriptions(data.subscriptions)
        setIsLoading(false)
      })
      .catch(e => {
        console.error(e)
        setIsLoading(false)
      })
  }, [siteSlug])

  useEffect(() => {
    getSettings()
  }, [siteSlug, getSettings])

  useEffect(() => {
    getAllUsersBySite()
  }, [siteSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchPressureMonitors = (showLoading = false) => {
      if (showLoading) setIsPressureMonitorsLoading(true)
      return api
        .getAllPressureMonitors(
          createQueryString({ siteSlug, perPage: 'all' })
        )
        .then(data => {
          setPressureMonitors(data.items)
          setIsPressureMonitorsLoading(false)
        })
        .catch(e => {
          console.error(e)
          setIsPressureMonitorsLoading(false)
        })
    }

    fetchPressureMonitors(true)
    // Keep online status current while the tab stays open.
    const interval = setInterval(fetchPressureMonitors, 30 * 1000)
    return () => clearInterval(interval)
  }, [siteSlug])

  const handleAddRecipient = () => {
    if (!selectedUserId) return

    api
      .addPressureMonitorAlertRecipient(siteSlug, selectedUserId)
      .then(() => {
        setSelectedUserId(undefined)
        getSettings()
      })
      .catch(e => console.error(e))
  }

  const handleRemoveRecipient = recipientId => {
    api
      .removePressureMonitorAlertRecipient(siteSlug, recipientId)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  const handleToggleSubscription = (alertType, active) => {
    api
      .updatePressureMonitorAlertSubscription(siteSlug, alertType, active)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  if (isLoading) {
    return <Spin size="large" className="w-100 center mv5" />
  }

  const availableUsers = siteUsers.filter(
    user => !recipients.some(recipient => recipient.userId === user.id)
  )

  const recipientUsers = recipients.map(recipient => {
    const user = siteUsers.find(u => u.id === recipient.userId)

    return {
      ...user,
      id: recipient.id,
      slug: (user && user.slug) || `recipient-${recipient.id}`,
      fullName: (user && user.fullName) || recipient.userName,
    }
  })

  const visiblePressureMonitors = showUnassigned
    ? pressureMonitors
    : pressureMonitors.filter(
        pressureMonitor => pressureMonitor.locationId
      )

  return (
    <div className="pa black-80">
      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Pressure Monitor Alert Recipients</H3>
        {canEdit && (
          <div className="flex items-center mb3">
            <AntSelect
              className="w-40 mr2"
              showSearch
              allowClear
              placeholder="Select a user"
              value={selectedUserId}
              onChange={setSelectedUserId}
              optionFilterProp="children"
            >
              {availableUsers.map(user => (
                <Option value={user.id} key={user.id}>
                  {user.firstName} {user.lastName}
                </Option>
              ))}
            </AntSelect>
            <Button
              text="Add Recipient"
              onClick={handleAddRecipient}
              disabled={!selectedUserId}
            />
          </div>
        )}
        <SiteUsers
          users={recipientUsers}
          getAllUsersBySite={getAllUsersBySite}
          deleteUserFromSite={canEdit ? handleRemoveRecipient : undefined}
        />
      </div>

      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Alerts</H3>
        {subscriptions.map(subscription => (
          <div
            key={subscription.alertType}
            className="flex justify-between items-center pv1 bb b--black-10"
          >
            <span>
              {PRESSURE_MONITOR_ALERT_TYPE_LABELS[subscription.alertType] ||
                subscription.alertType}
              {PRESSURE_MONITOR_ALERT_TYPE_DESCRIPTIONS[
                subscription.alertType
              ] && (
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      {PRESSURE_MONITOR_ALERT_TYPE_DESCRIPTIONS[
                        subscription.alertType
                      ]}
                    </div>
                  }
                  trigger="click"
                >
                  <Icon type="question-circle-o" className="ml2 pointer" />
                </Popover>
              )}
            </span>
            <Switch
              checked={subscription.active}
              disabled={!canEdit}
              onChange={checked =>
                handleToggleSubscription(subscription.alertType, checked)
              }
            />
          </div>
        ))}
      </div>

      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <div className="flex justify-between items-center mb2">
          <H3 className="ma0 f4">Pressure Monitors</H3>
          <div className="flex items-center">
            <span className="mr2">Display Unassigned Pressure Monitors</span>
            <Switch checked={showUnassigned} onChange={setShowUnassigned} />
          </div>
        </div>
        {isPressureMonitorsLoading ? (
          <Spin size="large" className="w-100 center mv4" />
        ) : (
          <Table
            dataSource={visiblePressureMonitors}
            rowKey="id"
            pagination={false}
            bordered
            tableLayout="auto"
            expandedRowRender={record => {
              const units =
                PRESSURE_MONITOR_MEASUREMENT_UNITS[
                  record.measurementUnits != null
                    ? record.measurementUnits
                    : 1
                ]
              return (
                <div className="flex flex-wrap">
                  <Block label="Module ID" size={SIZES.SMALL}>
                    {record.moduleId || '--'}
                  </Block>
                  <Block label="Activate Threshold" size={SIZES.SMALL}>
                    {record.activateThreshold != null
                      ? `${record.activateThreshold} ${units}`
                      : '--'}
                  </Block>
                  <Block label="Deactivate Threshold" size={SIZES.SMALL}>
                    {record.deactivateThreshold != null
                      ? `${record.deactivateThreshold} ${units}`
                      : '--'}
                  </Block>
                  <Block label="Audio Alert" size={SIZES.SMALL}>
                    {audioAlertStatus(record.audioAlert)}
                  </Block>
                  <Block label="Measurement Units" size={SIZES.SMALL}>
                    {units}
                  </Block>
                  <Block label="Violation Delay (sec)" size={SIZES.SMALL}>
                    {record.violationDelay != null
                      ? record.violationDelay
                      : '--'}
                  </Block>
                  <Block label="Cooldown Delay (sec)" size={SIZES.SMALL}>
                    {record.cooldownDelayS != null
                      ? record.cooldownDelayS
                      : '--'}
                  </Block>
                  <Block label="Audible Delay (sec)" size={SIZES.SMALL}>
                    {record.audibleDelay != null ? record.audibleDelay : '--'}
                  </Block>
                  <Block label="Cancel Delay (sec)" size={SIZES.SMALL}>
                    {record.cancelDelay != null ? record.cancelDelay : '--'}
                  </Block>
                </div>
              )
            }}
          >
            <Column
              title="Pillar ID"
              dataIndex="pillarId"
              sorter={sortBy('pillarId')}
              render={(text, record) => (
                <Anchor to={`/inventory/pressure-monitors/${record.id}`}>
                  {text}
                </Anchor>
              )}
            />
            <Column
              title="Location"
              dataIndex="locationName"
              sorter={sortBy('locationName')}
              render={(text, record) => (
                <div>
                  <div>
                    {text && record.floorId && record.locationId ? (
                      <Anchor
                        to={`/sites/${siteSlug}/floors/${record.floorId}/locations/${record.locationId}`}
                      >
                        {text}
                      </Anchor>
                    ) : (
                      text || 'Unassigned'
                    )}
                  </div>
                  {(record.buildingName || record.floorName) && (
                    <div className="f7 gray">
                      {record.buildingId ? (
                        <Anchor
                          to={`/sites/${siteSlug}/buildings/${record.buildingId}`}
                        >
                          {record.buildingName}
                        </Anchor>
                      ) : (
                        record.buildingName
                      )}
                      {record.buildingName && record.floorName && ' · '}
                      {record.floorId ? (
                        <Anchor
                          to={`/sites/${siteSlug}/floors/${record.floorId}`}
                        >
                          {record.floorName}
                        </Anchor>
                      ) : (
                        record.floorName
                      )}
                    </div>
                  )}
                </div>
              )}
            />
            <Column
              title="Status"
              dataIndex="status"
              sorter={sortBy('status')}
            />
            <Column
              title="Online"
              dataIndex="online"
              sorter={sortBy('online')}
              render={online =>
                online ? (
                  <Tooltip title="Online">
                    <Icon type="check-circle-o" className="green" />
                    <span className="pl2">Online</span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Offline">
                    <Icon type="poweroff" className="red" />
                    <span className="pl2">Offline</span>
                  </Tooltip>
                )
              }
            />
            <Column
              width={60}
              fixed="right"
              render={(text, record) => (
                <Anchor to={`/inventory/pressure-monitors/${record.id}/edit`}>
                  Edit
                </Anchor>
              )}
            />
          </Table>
        )}
      </div>
    </div>
  )
}

PressureMonitorAlertsSettings.propTypes = {
  siteSlug: PropTypes.string.isRequired,
  siteUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  getAllUsersBySite: PropTypes.func.isRequired,
}

export default PressureMonitorAlertsSettings
